/**
 * Proper court-name formatting for the Court surfaces of the eFiling wizard.
 *
 * Originally scoped to the consolidated Confirm-your-filing flow (Walter R80,
 * scoping the R72/R73 fixes to `?confirmFlow=1`) while the classic flow's
 * Court card stayed byte-identical through the 07-16 session freeze. R109
 * (Walter, post-session) lifts that freeze for the card label alone: the
 * classic card now renders the title-cased `formatCountyStateLabel` instead
 * of the raw "madera, CA". Display-only: persisted wizard state
 * (courtJurisdiction.code / .name, the county slug) and everything the
 * envelope reads are never formatted.
 */

const titleCase = (value: string): string =>
  value
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Short "<County>, <ST>" label for a detected county (R109): the classic
 * Court card's compact form of the court, title-cased from the lowercase
 * wizard slug so it matches the canonical name shown in the Filing Summary
 * ("madera" → "Madera, CA", "los-angeles" → "Los Angeles, CA").
 */
export function formatCountyStateLabel(county: string, state?: string): string {
  const countyName = titleCase(county);
  if (!countyName) return '';
  const stateCode = (state || 'CA').trim().toUpperCase();
  return `${countyName}, ${stateCode}`;
}

/**
 * Full proper court name for a detected county (R72): the wizard's county is
 * a lowercase slug ("madera", "los-angeles"), never a display name.
 * California counties — the only ones the product files in today — render as
 * the official style: "Superior Court of California, County of Madera".
 */
export function formatDetectedCourtName(county: string, state?: string): string {
  const countyName = titleCase(county);
  if (!countyName) return '';
  const stateCode = (state || 'CA').trim().toUpperCase();
  if (stateCode === 'CA') {
    return `Superior Court of California, County of ${countyName}`;
  }
  return `${countyName} County, ${stateCode}`;
}

/**
 * Display label for a Court Jurisdiction option (R73). The EFM policy's
 * COURT_LOCATIONS names are internal tokens ("Madera Courthouse", code "M"),
 * not court names. When the option name carries no information beyond the
 * county itself, render the proper court name alone; otherwise keep the
 * courthouse as a suffix so multi-courthouse counties (e.g. Alameda) stay
 * distinguishable in the select.
 */
export function formatCourtJurisdictionName(
  rawName: string,
  county: string,
  state?: string,
): string {
  const courtName = formatDetectedCourtName(county, state);
  const name = rawName.trim();
  if (!courtName) return name;

  const normalized = name.toLowerCase();
  const countyNormalized = titleCase(county).toLowerCase();
  const redundant =
    name.length <= 2 || // a bare location code like "M"
    normalized === countyNormalized ||
    normalized === `${countyNormalized} courthouse` ||
    normalized === `${countyNormalized} superior court`;

  return !name || redundant ? courtName : `${courtName} — ${name}`;
}
